<template>
    <div class="pref-card mb-3">
        <!-- Header: label + position + delete -->
        <div class="pref-header">
            <div class="pref-header-left">
                <input
                    type="text"
                    class="form-control pref-label"
                    v-model="local.label"
                    @blur="saveIfDirty('label')"
                    maxlength="255"
                    :placeholder="$t('spheres.preferenceCard.sectionLabelPlaceholder')"
                />
            </div>
            <div class="pref-header-right">
                <div class="pref-pos">
                    <label>{{ $t('spheres.preferenceCard.positionLabel') }}</label>
                    <input
                        type="number"
                        v-model.number="local.position"
                        @blur="saveIfDirty('position')"
                        min="0"
                    />
                </div>
                <button
                    type="button"
                    class="pref-delete"
                    @click="confirmingDelete = true"
                    :title="$t('spheres.preferenceCard.deleteSectionTitle')"
                >
                    <i class="bi bi-trash"></i>
                </button>
            </div>
        </div>

        <!-- Object naming for step 3 (Список). NULL falls back to the sphere
             default. Children inherit from this main preference automatically. -->
        <div class="pref-section">
            <div class="pref-section-label">
                <i class="bi bi-tag"></i>
                {{ $t('spheres.preferenceCard.objectNaming.title') }}
                <span class="pref-section-meta">
                    {{ $t('spheres.preferenceCard.objectNaming.meta') }}
                </span>
            </div>
            <div class="naming-grid">
                <div class="naming-field">
                    <label class="naming-label">{{ $t('spheres.preferenceCard.objectNaming.singularLabel') }}</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="local.object_label_singular"
                        @blur="saveIfDirty('object_label_singular')"
                        maxlength="64"
                        :placeholder="$t('spheres.preferenceCard.objectNaming.singularPlaceholder')"
                    />
                </div>
                <div class="naming-field">
                    <label class="naming-label">{{ $t('spheres.preferenceCard.objectNaming.pluralLabel') }}</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="local.object_label_plural"
                        @blur="saveIfDirty('object_label_plural')"
                        maxlength="64"
                        :placeholder="$t('spheres.preferenceCard.objectNaming.pluralPlaceholder')"
                    />
                </div>
            </div>
        </div>

        <!-- Per-preference step-3 ("Список") texts, localized RU/KZ/EN -->
        <div class="pref-section">
            <div class="pref-section-label">
                <i class="bi bi-card-heading"></i>
                {{ $t('spheres.preferenceCard.stepTexts.title') }}
                <span class="pref-section-meta">
                    {{ $t('spheres.preferenceCard.stepTexts.meta') }}
                </span>
            </div>
            <div class="so-locale-tabs">
                <button
                    v-for="l in ['ru', 'kz', 'en']"
                    :key="l"
                    type="button"
                    class="so-tab"
                    :class="{ active: soLocale === l }"
                    @click="soLocale = l"
                >{{ l.toUpperCase() }}</button>
            </div>
            <div class="naming-field">
                <label class="naming-label">{{ $t('spheres.preferenceCard.stepTexts.headingLabel') }}</label>
                <input
                    type="text"
                    class="form-control"
                    maxlength="200"
                    v-model="local.step_overrides.heading[soLocale]"
                    @blur="saveStepOverrides()"
                    :placeholder="$t('spheres.preferenceCard.stepTexts.headingPlaceholder')"
                />
            </div>
            <div class="naming-field">
                <label class="naming-label">{{ $t('spheres.preferenceCard.stepTexts.hintLabel') }}</label>
                <input
                    type="text"
                    class="form-control"
                    maxlength="500"
                    v-model="local.step_overrides.hint[soLocale]"
                    @blur="saveStepOverrides()"
                    :placeholder="$t('spheres.preferenceCard.stepTexts.hintPlaceholder')"
                />
            </div>
            <div class="naming-field">
                <label class="naming-label">{{ $t('spheres.preferenceCard.stepTexts.placeholderLabel') }}</label>
                <input
                    type="text"
                    class="form-control"
                    maxlength="100"
                    v-model="local.step_overrides.name_placeholder[soLocale]"
                    @blur="saveStepOverrides()"
                    :placeholder="$t('spheres.preferenceCard.stepTexts.placeholderPlaceholder')"
                />
            </div>
        </div>

        <!-- Recommended widgets: compact list + "+" button -->
        <div class="pref-section">
            <div class="pref-section-label">
                <i class="bi bi-grid-3x3-gap"></i>
                {{ $t('spheres.preferenceCard.widgets.title') }}
                <span class="pref-section-meta" v-if="!sphereWidgets.length">
                    {{ $t('spheres.preferenceCard.widgets.metaEmpty') }}
                </span>
                <span class="pref-section-meta" v-else-if="recommendedFor(local).length === 0">
                    {{ $t('spheres.preferenceCard.widgets.metaNone') }}
                </span>
                <span class="pref-section-meta" v-else>
                    {{ $t('spheres.preferenceCard.widgets.metaCount', { count: recommendedFor(local).length, total: sphereWidgets.length }) }}
                </span>
            </div>
            <div v-if="sphereWidgets.length" class="rec-row">
                <div
                    v-for="w in recommendedFor(local)"
                    :key="w.sphere_widget_id"
                    class="rec-chip"
                >
                    <img v-if="w.picture" :src="displayPicture(w.picture)" class="rec-chip-pic" :alt="w.name" @error="onPicError" />
                    <span class="rec-chip-name">{{ w.name }}</span>
                    <button
                        type="button"
                        class="rec-chip-x"
                        @click="toggleRecommendation(local, w, false)"
                        :title="$t('spheres.preferenceCard.widgets.remove', { name: w.name })"
                    >
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
                <button
                    type="button"
                    class="rec-add-btn"
                    :disabled="availableFor(local).length === 0"
                    @click="openPicker(local)"
                >
                    <i class="bi bi-plus-lg"></i>
                    {{ recommendedFor(local).length === 0 ? $t('spheres.preferenceCard.widgets.chooseButton') : $t('spheres.preferenceCard.widgets.addButton') }}
                </button>
            </div>
            <div v-else class="chip-empty">
                {{ $t('spheres.preferenceCard.widgets.empty') }}
            </div>
        </div>

        <!-- Children -->
        <div v-if="local.children && local.children.length" class="pref-children">
            <div class="pref-children-label">
                <i class="bi bi-arrow-return-right"></i>
                {{ $t('spheres.preferenceCard.children.title') }}
            </div>
            <div
                v-for="child in local.children"
                :key="child.id"
                class="child-card"
            >
                <div class="pref-header">
                    <div class="pref-header-left">
                        <input
                            type="text"
                            class="form-control pref-label"
                            v-model="child.label"
                            @blur="saveChildIfDirty(child, 'label')"
                            maxlength="255"
                            :placeholder="$t('spheres.preferenceCard.children.labelPlaceholder')"
                        />
                    </div>
                    <div class="pref-header-right">
                        <div class="pref-pos">
                            <label>{{ $t('spheres.preferenceCard.positionLabel') }}</label>
                            <input
                                type="number"
                                v-model.number="child.position"
                                @blur="saveChildIfDirty(child, 'position')"
                                min="0"
                            />
                        </div>
                        <button
                            type="button"
                            class="pref-delete"
                            @click="confirmDeleteChild(child)"
                            :title="$t('spheres.preferenceCard.children.deleteTitle')"
                        >
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                </div>
                <div class="pref-section">
                    <div class="pref-section-label">
                        <i class="bi bi-grid-3x3-gap"></i>
                        {{ $t('spheres.preferenceCard.widgets.title') }}
                        <span class="pref-section-meta" v-if="recommendedFor(child).length === 0">{{ $t('spheres.preferenceCard.widgets.metaNone') }}</span>
                        <span class="pref-section-meta" v-else>{{ $t('spheres.preferenceCard.widgets.metaCount', { count: recommendedFor(child).length, total: sphereWidgets.length }) }}</span>
                    </div>
                    <div v-if="sphereWidgets.length" class="rec-row">
                        <div
                            v-for="w in recommendedFor(child)"
                            :key="w.sphere_widget_id"
                            class="rec-chip"
                        >
                            <img v-if="w.picture" :src="displayPicture(w.picture)" class="rec-chip-pic" :alt="w.name" @error="onPicError" />
                            <span class="rec-chip-name">{{ w.name }}</span>
                            <button
                                type="button"
                                class="rec-chip-x"
                                @click="toggleRecommendation(child, w, false)"
                                :title="$t('spheres.preferenceCard.widgets.remove', { name: w.name })"
                            >
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <button
                            type="button"
                            class="rec-add-btn"
                            :disabled="availableFor(child).length === 0"
                            @click="openPicker(child)"
                        >
                            <i class="bi bi-plus-lg"></i>
                            {{ recommendedFor(child).length === 0 ? $t('spheres.preferenceCard.widgets.chooseButton') : $t('spheres.preferenceCard.widgets.addButton') }}
                        </button>
                    </div>
                    <div v-else class="chip-empty">
                        {{ $t('spheres.preferenceCard.widgets.empty') }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Add child -->
        <div class="pref-add-child">
            <button
                v-if="!addingChild"
                type="button"
                class="btn-add-child"
                @click="addingChild = true"
            >
                <i class="bi bi-plus-circle"></i> {{ $t('spheres.preferenceCard.children.addButton') }}
            </button>
            <div v-else class="add-child-form">
                <input
                    type="text"
                    class="form-control"
                    v-model="newChildLabel"
                    :placeholder="$t('spheres.preferenceCard.children.labelPlaceholder')"
                    maxlength="255"
                    @keyup.enter="addChild"
                    autofocus
                />
                <button type="button" class="btn btn-primary btn-sm" @click="addChild" :disabled="!newChildLabel.trim()">
                    {{ $t('spheres.preferenceCard.children.addConfirm') }}
                </button>
                <button type="button" class="btn btn-link btn-sm text-muted" @click="cancelAddChild">
                    {{ $t('spheres.preferenceCard.children.cancel') }}
                </button>
            </div>
        </div>
    </div>

    <Modal2
        v-model="confirmingDelete"
        :title="$t('spheres.preferenceCard.deleteModal.title', { label: local.label })"
        :content="$t('spheres.preferenceCard.deleteModal.content')"
        @confirm="doDelete"
    />
    <Modal2
        v-model="confirmingDeleteChild"
        :title="$t('spheres.preferenceCard.deleteModal.title', { label: childToDelete ? childToDelete.label : '' })"
        :content="$t('spheres.preferenceCard.deleteChildModal.content')"
        @confirm="doDeleteChild"
    />

    <!-- Inline picker: choose which of the sphere's catalog widgets to
         recommend for the currently-targeted preference (main or child). -->
    <Modal2
        v-model="pickerOpen"
        :title="$t('spheres.preferenceCard.widgetPickerModal.title', { label: pickerTarget ? pickerTarget.label : '' })"
        size="lg"
    >
        <template #body>
            <div v-if="!pickerAvailable.length" class="picker-empty-inline">
                {{ $t('spheres.preferenceCard.widgetPickerModal.empty') }}
            </div>
            <div v-else class="picker-inline-grid">
                <label
                    v-for="w in pickerAvailable"
                    :key="w.sphere_widget_id"
                    class="picker-inline-chip"
                    :class="{ 'picker-inline-chip-active': pickerSelected.has(w.sphere_widget_id) }"
                >
                    <input
                        type="checkbox"
                        :checked="pickerSelected.has(w.sphere_widget_id)"
                        @change="togglePickerSelection(w)"
                    />
                    <img v-if="w.picture" :src="displayPicture(w.picture)" class="picker-inline-pic" :alt="w.name" @error="onPicError" />
                    <span class="picker-inline-name">{{ w.name }}</span>
                    <span class="picker-inline-type">{{ w.widget_type }}</span>
                </label>
            </div>
        </template>
        <template #footer>
            <button type="button" class="btn-ghost" @click="closePicker">{{ $t('spheres.preferenceCard.widgetPickerModal.cancel') }}</button>
            <button
                type="button"
                class="btn-primary-modal"
                :disabled="!pickerSelected.size"
                @click="confirmPicker"
            >
                <i class="bi bi-plus-lg"></i>
                {{ $t('spheres.preferenceCard.widgetPickerModal.addButton') }}{{ pickerSelected.size ? ' (' + pickerSelected.size + ')' : '' }}
            </button>
        </template>
    </Modal2>
</template>

<script>
import Modal2 from '../Modal2.vue'

export default {
    name: 'PreferenceCard',
    components: { Modal2 },
    props: {
        preference: { type: Object, required: true },
        sphereWidgets: { type: Array, required: true },
        sphereId: { type: Number, required: true }
    },
    emits: ['changed'],
    data() {
        const local = this.cloneDeep(this.preference)
        local.step_overrides = this.normSO(this.preference.step_overrides)
        return {
            local,
            originalLabel: this.preference.label,
            originalPosition: this.preference.position,
            originalSingular: this.preference.object_label_singular || '',
            originalPlural: this.preference.object_label_plural || '',
            soLocale: 'ru',
            originalStepOverrides: JSON.stringify(this.cleanSO(local.step_overrides)),
            childOriginals: {},
            addingChild: false,
            newChildLabel: '',
            confirmingDelete: false,
            confirmingDeleteChild: false,
            childToDelete: null,
            // Picker state — single modal shared between main pref + children.
            pickerOpen: false,
            pickerTarget: null,   // ref to the preference being edited
            pickerSelected: new Set() // sphere_widget_id ints
        }
    },
    watch: {
        preference: {
            handler(v) {
                this.local = this.cloneDeep(v)
                this.local.step_overrides = this.normSO(v.step_overrides)
                this.originalLabel = v.label
                this.originalPosition = v.position
                this.originalSingular = v.object_label_singular || ''
                this.originalPlural = v.object_label_plural || ''
                this.originalStepOverrides = JSON.stringify(this.cleanSO(this.local.step_overrides))
                this.childOriginals = {}
                if (v.children) {
                    for (const c of v.children) {
                        this.childOriginals[c.id] = { label: c.label, position: c.position }
                    }
                }
            },
            deep: true
        }
    },
    computed: {
        // Widgets the user can still pick for the current picker target.
        // (sphereWidgets minus those already recommended for the target.)
        pickerAvailable() {
            if (!this.pickerTarget) return []
            return this.availableFor(this.pickerTarget)
        }
    },
    methods: {
        cloneDeep(o) {
            return JSON.parse(JSON.stringify(o))
        },
        // Normalize a stored step_overrides blob into the full editable shape
        // (every field/locale present, so v-model bindings never hit undefined).
        normSO(so) {
            const lt = f => ({
                ru: (so && so[f] && so[f].ru) || '',
                kz: (so && so[f] && so[f].kz) || '',
                en: (so && so[f] && so[f].en) || '',
            })
            return { heading: lt('heading'), hint: lt('hint'), name_placeholder: lt('name_placeholder') }
        },
        // Inverse: strip empty locales/fields; return null if everything is empty
        // (so the column reverts to NULL and the wizard inherits from the sphere).
        cleanSO(so) {
            const out = {}
            let any = false
            for (const field of ['heading', 'hint', 'name_placeholder']) {
                const v = (so && so[field]) || {}
                const locales = {}
                let anyLocale = false
                for (const loc of ['ru', 'kz', 'en']) {
                    const s = (v[loc] || '').trim()
                    if (s) { locales[loc] = s; anyLocale = true }
                }
                if (anyLocale) { out[field] = locales; any = true }
            }
            return any ? out : null
        },
        async saveStepOverrides() {
            const cleaned = this.cleanSO(this.local.step_overrides)
            const serialized = JSON.stringify(cleaned)
            if (serialized === this.originalStepOverrides) return
            const r = await this.$api.patchPreference(this.sphereId, this.local.id, { step_overrides: cleaned })
            if (r.result === 'SUCCESS') {
                this.originalStepOverrides = serialized
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        // Cabinet stores pictures as relative paths (../images/widget/x.svg).
        // Rewrite to absolute cabinet URLs for the admin preview.
        displayPicture(p) {
            if (!p) return ''
            if (/^https?:\/\//.test(p)) return p
            if (p.startsWith('data:')) return p
            const cleaned = p.replace(/^(\.\.\/)+/, '/')
            const path = cleaned.startsWith('/') ? cleaned : '/' + cleaned
            return 'https://cabinet.superqr.com' + path
        },
        onPicError(e) { e.target.style.opacity = 0.25 },
        // Filter sphereWidgets to the subset currently recommended for `target`.
        recommendedFor(target) {
            return this.sphereWidgets.filter(w => this.isRecommended(target, w))
        },
        // Inverse: subset NOT yet recommended.
        availableFor(target) {
            return this.sphereWidgets.filter(w => !this.isRecommended(target, w))
        },
        openPicker(target) {
            this.pickerTarget = target
            this.pickerSelected = new Set()
            this.pickerOpen = true
        },
        closePicker() {
            this.pickerOpen = false
            this.pickerTarget = null
            this.pickerSelected = new Set()
        },
        togglePickerSelection(w) {
            const id = w.sphere_widget_id
            if (this.pickerSelected.has(id)) {
                this.pickerSelected.delete(id)
            } else {
                this.pickerSelected.add(id)
            }
            this.pickerSelected = new Set(this.pickerSelected)
        },
        async confirmPicker() {
            const target = this.pickerTarget
            const toAdd = this.pickerAvailable.filter(w => this.pickerSelected.has(w.sphere_widget_id))
            this.closePicker()
            // toggleRecommendation already does API + optimistic update + emit.
            for (const w of toAdd) {
                await this.toggleRecommendation(target, w, true)
            }
        },
        // Match a sphere_widget against the preference's recommendations.
        // Recommendations are stored as instance-level rows when possible
        // (recommended_widget_instances); legacy NULL-catalog_widget_id rows
        // still match any sphere widget of the same type.
        isRecommended(target, w) {
            const instances = target.recommended_widget_instances || []
            for (const rec of instances) {
                if (rec.widget_type !== w.widget_type) continue
                // Both NULL → legacy type-level match
                if (rec.catalog_widget_id == null && w.catalog_widget_id == null) return true
                // Same instance id → exact match
                if (rec.catalog_widget_id != null && rec.catalog_widget_id === w.catalog_widget_id) return true
                // Legacy rec (NULL) matches any instance of the same type
                if (rec.catalog_widget_id == null) return true
            }
            return false
        },
        async saveIfDirty(field) {
            if (field === 'label' && this.local.label === this.originalLabel) return
            if (field === 'position' && this.local.position === this.originalPosition) return
            if (field === 'object_label_singular' && (this.local.object_label_singular || '') === this.originalSingular) return
            if (field === 'object_label_plural' && (this.local.object_label_plural || '') === this.originalPlural) return

            let data
            if (field === 'label') data = { label: this.local.label }
            else if (field === 'position') data = { position: this.local.position }
            else data = { [field]: this.local[field] || null }

            const r = await this.$api.patchPreference(this.sphereId, this.local.id, data)
            if (r.result === 'SUCCESS') {
                // Update originals so subsequent blurs see no diff. NOT emitting
                // 'changed' — local state already reflects the new value, and
                // re-fetching the whole sphere would unmount the section and
                // jump the user's scroll to the top.
                if (field === 'label') this.originalLabel = this.local.label
                else if (field === 'position') this.originalPosition = this.local.position
                else if (field === 'object_label_singular') this.originalSingular = this.local.object_label_singular || ''
                else if (field === 'object_label_plural') this.originalPlural = this.local.object_label_plural || ''
            } else {
                this.$notify({ text: r.msg, type: 'error' })
                if (field === 'label') this.local.label = this.originalLabel
                else if (field === 'position') this.local.position = this.originalPosition
                else if (field === 'object_label_singular') this.local.object_label_singular = this.originalSingular
                else if (field === 'object_label_plural') this.local.object_label_plural = this.originalPlural
            }
        },
        async saveChildIfDirty(child, field) {
            const orig = this.childOriginals[child.id] || {}
            if (field === 'label' && child.label === orig.label) return
            if (field === 'position' && child.position === orig.position) return
            const data = field === 'label' ? { label: child.label } : { position: child.position }
            const r = await this.$api.patchPreference(this.sphereId, child.id, data)
            if (r.result === 'SUCCESS') {
                // Update original in place so the next blur sees no diff.
                // No emit — see saveIfDirty for the rationale.
                if (!this.childOriginals[child.id]) this.childOriginals[child.id] = {}
                this.childOriginals[child.id][field] = child[field]
            } else {
                this.$notify({ text: r.msg, type: 'error' })
                child[field] = orig[field]
            }
        },
        async toggleRecommendation(target, w, checked) {
            const r = checked
                ? await this.$api.addRecommendation(this.sphereId, target.id, w.widget_type, w.catalog_widget_id)
                : await this.$api.removeRecommendation(this.sphereId, target.id, w.widget_type, w.catalog_widget_id)
            if (r.result === 'SUCCESS') {
                // Optimistic local update — full refetch follows on 'changed'.
                if (!target.recommended_widget_instances) target.recommended_widget_instances = []
                if (!target.recommended_widgets) target.recommended_widgets = []
                if (checked) {
                    target.recommended_widget_instances.push({
                        widget_type: w.widget_type,
                        catalog_widget_id: w.catalog_widget_id,
                        title: w.title || w.name,
                        picture: w.picture,
                    })
                    if (target.recommended_widgets.indexOf(w.widget_type) === -1) {
                        target.recommended_widgets.push(w.widget_type)
                    }
                } else {
                    target.recommended_widget_instances = target.recommended_widget_instances.filter(rec => {
                        if (rec.widget_type !== w.widget_type) return true
                        if (rec.catalog_widget_id == null && w.catalog_widget_id == null) return false
                        if (rec.catalog_widget_id === w.catalog_widget_id) return false
                        return true
                    })
                    // Recompute the type list from the remaining instances.
                    const types = []
                    for (const rec of target.recommended_widget_instances) {
                        if (types.indexOf(rec.widget_type) === -1) types.push(rec.widget_type)
                    }
                    target.recommended_widgets = types
                }
                // No emit: local optimistic update is enough. Reloading the
                // entire sphere just to refresh a chip would jump the user's
                // scroll. Parent re-fetches on real structural changes only.
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        async addChild() {
            const label = this.newChildLabel.trim()
            if (!label) return
            const r = await this.$api.addPreference(this.sphereId, {
                label,
                parent_id: this.local.id,
                position: this.local.children ? this.local.children.length : 0
            })
            if (r.result === 'SUCCESS') {
                this.newChildLabel = ''
                this.addingChild = false
                this.$emit('changed')
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        cancelAddChild() {
            this.addingChild = false
            this.newChildLabel = ''
        },
        async doDelete() {
            const r = await this.$api.deletePreference(this.sphereId, this.local.id)
            if (r.result === 'SUCCESS') {
                this.$emit('changed')
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        confirmDeleteChild(child) {
            this.childToDelete = child
            this.confirmingDeleteChild = true
        },
        async doDeleteChild() {
            if (!this.childToDelete) return
            const r = await this.$api.deletePreference(this.sphereId, this.childToDelete.id)
            this.childToDelete = null
            if (r.result === 'SUCCESS') {
                this.$emit('changed')
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        }
    }
}
</script>

<style scoped>
.pref-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px 20px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.pref-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

/* Header row */
.pref-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
}
.pref-header-left {
    flex: 1;
    min-width: 0;
}
.pref-label {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    border: 1px solid transparent;
    background: transparent;
    padding: 6px 8px;
    border-radius: 8px;
    transition: background 0.15s ease, border-color 0.15s ease;
}
.pref-label:hover {
    background: #f8fafc;
}
.pref-label:focus {
    background: #fff;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
    outline: none;
}
.pref-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}
.pref-pos {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #f1f5f9;
    border-radius: 8px;
    padding: 4px 10px;
}
.pref-pos label {
    font-size: 12px;
    color: #64748b;
    margin: 0;
    font-weight: 500;
}
.pref-pos input {
    width: 44px;
    border: none;
    background: transparent;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    padding: 2px 0;
}
.pref-pos input:focus {
    outline: none;
}
.pref-delete {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #fecaca;
    background: #fff;
    color: #dc2626;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}
.pref-delete:hover {
    background: #fef2f2;
    border-color: #dc2626;
}

/* Section labels */
.pref-section {
    margin-top: 4px;
}
.pref-section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 10px;
}
.pref-section-label i {
    font-size: 14px;
    color: #7c3aed;
}
.pref-section-meta {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
    color: #94a3b8;
    margin-left: 4px;
}

/* Object naming inputs */
.naming-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 4px;
}
.naming-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.naming-label {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
    margin: 0;
    text-transform: none;
    letter-spacing: 0;
}
.naming-field input {
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 13px;
    color: #0f172a;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.naming-field input:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}
.naming-field input::placeholder {
    color: #cbd5e1;
}
@media (max-width: 520px) {
    .naming-grid { grid-template-columns: 1fr; }
}

/* RU/KZ/EN locale tabs for the step-3 text overrides */
.so-locale-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
}
.so-tab {
    border: 1.5px solid #e5e7eb;
    background: #fff;
    color: #64748b;
    border-radius: 7px;
    padding: 3px 12px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
}
.so-tab.active {
    border-color: #7c3aed;
    background: #7c3aed;
    color: #fff;
}
.pref-section .naming-field + .naming-field {
    margin-top: 8px;
}

/* Compact recommended-widgets row */
.rec-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
}
.rec-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 4px 4px 6px;
    background: #f5f0ff;
    border: 1.5px solid #c4b5fd;
    border-radius: 999px;
    color: #5b21b6;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
}
.rec-chip-pic {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    object-fit: contain;
    background: #fff;
    flex-shrink: 0;
}
.rec-chip-name {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.rec-chip-x {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #7c3aed;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    transition: background 0.12s ease, color 0.12s ease;
    padding: 0;
}
.rec-chip-x:hover {
    background: #7c3aed;
    color: #fff;
}
.rec-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: #fff;
    border: 1.5px dashed #c4b5fd;
    border-radius: 999px;
    color: #6d28d9;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.12s ease;
}
.rec-add-btn:hover:not(:disabled) {
    background: #faf7ff;
    border-color: #7c3aed;
    border-style: solid;
}
.rec-add-btn:disabled {
    color: #cbd5e1;
    border-color: #e5e7eb;
    cursor: not-allowed;
}

/* Picker modal */
.picker-empty-inline {
    padding: 24px;
    text-align: center;
    color: #94a3b8;
    font-size: 13px;
    background: #f8fafc;
    border-radius: 10px;
}
.picker-inline-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px;
    max-height: 50vh;
    overflow-y: auto;
    padding-right: 4px;
}
.picker-inline-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.12s ease;
    margin: 0;
}
.picker-inline-chip:hover {
    border-color: #c4b5fd;
    background: #faf7ff;
}
.picker-inline-chip-active {
    border-color: #7c3aed;
    background: #f5f0ff;
}
.picker-inline-chip input[type=checkbox] {
    width: 16px;
    height: 16px;
    accent-color: #7c3aed;
    cursor: pointer;
    flex-shrink: 0;
}
.picker-inline-pic {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    object-fit: contain;
    background: #f8fafc;
    flex-shrink: 0;
}
.picker-inline-name {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.picker-inline-type {
    font-size: 10px;
    font-family: ui-monospace, monospace;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    flex-shrink: 0;
}
.picker-inline-chip-active .picker-inline-name {
    color: #6d28d9;
}
.picker-inline-chip-active .picker-inline-type {
    color: #7c3aed;
    background: #ede9fe;
}
.btn-primary-modal {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #7c3aed;
    border: 1px solid #7c3aed;
    border-radius: 9px;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(124, 58, 237, 0.2);
    transition: all 0.15s ease;
}
.btn-primary-modal:hover:not(:disabled) {
    background: #6d28d9;
    border-color: #6d28d9;
}
.btn-primary-modal:disabled {
    background: #c4b5fd;
    border-color: #c4b5fd;
    cursor: not-allowed;
    box-shadow: none;
}

/* Chip grid */
.chip-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 8px;
}
.chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.12s ease;
    user-select: none;
    margin: 0;
}
.chip input[type=checkbox] {
    width: 16px;
    height: 16px;
    accent-color: #7c3aed;
    cursor: pointer;
    flex-shrink: 0;
}
.chip-pic {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
    background: #f1f5f9;
    flex-shrink: 0;
}
.chip:hover {
    border-color: #c4b5fd;
    background: #faf7ff;
}
.chip-active {
    border-color: #7c3aed;
    background: #f5f0ff;
}
.chip-active .chip-name {
    color: #6d28d9;
}
.chip-name {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.chip-type {
    font-size: 10px;
    font-family: ui-monospace, monospace;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    flex-shrink: 0;
}
.chip-active .chip-type {
    color: #7c3aed;
    background: #ede9fe;
}
.chip-empty {
    padding: 16px;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    color: #94a3b8;
    font-size: 13px;
    text-align: center;
}

/* Children */
.pref-children {
    margin-top: 18px;
    padding-left: 16px;
    border-left: 3px solid #ede9fe;
}
.pref-children-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #7c3aed;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 10px;
}
.child-card {
    background: #fafbfc;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 10px;
}
.child-card:last-child {
    margin-bottom: 0;
}
.child-card .pref-header {
    margin-bottom: 12px;
}

/* Add child */
.pref-add-child {
    margin-top: 16px;
}
.btn-add-child {
    background: transparent;
    border: 1.5px dashed #cbd5e1;
    color: #64748b;
    padding: 8px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.btn-add-child:hover {
    border-color: #7c3aed;
    color: #7c3aed;
    background: #faf7ff;
}
.add-child-form {
    display: flex;
    gap: 8px;
    align-items: center;
}
.add-child-form input {
    flex: 1;
}
</style>
